import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, router } from '@inertiajs/react'
import { ExternalLink, Plug, RefreshCw, Trash2, Users } from 'lucide-react'
import { Badge } from '@/components/ui/badge'
import { Button, buttonVariants } from '@/components/ui/button'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { agentHueStyle } from '@/frontend/lib/agentHue'
import { submitComposioConnect } from '@/frontend/utils/composioConnect'
import { formatLastVerifiedShort, formatVerifiedAbsolute, submitComposioRefresh } from '@/frontend/utils/composioRefresh'
import type { ComposioConnectionStatus, WorkerConnectionStatus } from '@/frontend/pages/Dashboard/Composio/types'

interface ComposioWorkersTableProps {
  workers: WorkerConnectionStatus[]
  appSlug: string
  canManage: boolean
  connectable: boolean
}

const initialOf = (name: string) => name.trim().charAt(0).toUpperCase()

function statusBadge(status: string | null) {
  if (!status) return <Badge variant="secondary">Inativo</Badge>
  if (['ACTIVE'].includes(status)) return <Badge variant="success">Ativo</Badge>
  if (status === 'ERROR') return <Badge variant="destructive">Erro</Badge>
  return <Badge variant="secondary">Inativo</Badge>
}

function connectionStatusBadge(connection: ComposioConnectionStatus) {
  if (connection.connected) return <Badge variant="success">Ativo</Badge>
  if (connection.connection_status === 'ERROR') return <Badge variant="destructive">Erro</Badge>
  return <Badge variant="secondary">Inativo</Badge>
}

function formatConnectedDate(connectedAt: string | null) {
  return connectedAt ? new Date(connectedAt).toLocaleDateString('pt-BR') : '—'
}

function shouldAutoRefresh(connection: ComposioConnectionStatus) {
  if (!connection.connected) return false
  if (!connection.last_synced_at) return true

  const lastSyncedAt = new Date(connection.last_synced_at).getTime()
  if (Number.isNaN(lastSyncedAt)) return true

  return Date.now() - lastSyncedAt > 5 * 60 * 1000
}

interface ConnectionRowProps {
  worker: WorkerConnectionStatus
  connection: ComposioConnectionStatus
  canManage: boolean
  refreshingId: string | null
  connectingKey: string | null
  onRefresh: (connection: ComposioConnectionStatus) => void
  onDisconnect: (worker: WorkerConnectionStatus, connection: ComposioConnectionStatus) => void
  onReconnect: (worker: WorkerConnectionStatus, connection: ComposioConnectionStatus) => void
}

function ConnectionRow({
  worker,
  connection,
  canManage,
  refreshingId,
  connectingKey,
  onRefresh,
  onDisconnect,
  onReconnect,
}: ConnectionRowProps) {
  const refreshing = refreshingId === connection.connected_account_id
  const connectionActionPending = refreshing || Boolean(connectingKey)
  const syncedLabel = formatVerifiedAbsolute(connection.last_synced_at)

  return (
    <div
      data-testid={`composio-connection-${connection.connected_account_id}`}
      className="flex flex-col gap-3 rounded-md border border-border/70 bg-muted/20 p-3 sm:flex-row sm:items-center"
    >
      <div className="min-w-0 flex-1">
        <div className="flex flex-wrap items-center gap-2">
          <span className="truncate text-sm font-medium text-foreground">{connection.account_label}</span>
          {connectionStatusBadge(connection)}
        </div>
        <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-muted-foreground">
          <span>Conectado em {formatConnectedDate(connection.connected_at)}</span>
          <span title={syncedLabel}>{formatLastVerifiedShort(connection.last_synced_at)}</span>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-1 sm:ml-auto sm:justify-end">
        <Button
          variant="ghost"
          size="sm"
          disabled={!canManage || connectionActionPending}
          aria-label={`Forçar sync de ${connection.account_label}`}
          title="Forçar sync"
          onClick={() => onRefresh(connection)}
        >
          <RefreshCw className={`h-4 w-4 ${refreshing ? 'animate-spin' : ''}`} />
          <span className="sr-only">Forçar sync</span>
        </Button>

        {!connection.connected && (
          <Button
            variant="ghost"
            size="sm"
            disabled={!canManage || Boolean(connectingKey)}
            onClick={() => onReconnect(worker, connection)}
          >
            <ExternalLink className="h-4 w-4 mr-1" />
            Reconectar
          </Button>
        )}

        {/* Separador visual: isola a ação destrutiva das ações de sync/reconexão
            para reduzir cliques acidentais agora que "Remover" fica ao lado. */}
        <span aria-hidden="true" className="mx-0.5 hidden h-5 w-px bg-border sm:block" />

        <Button
          variant="ghost"
          size="sm"
          disabled={!canManage || Boolean(connectingKey)}
          className="text-muted-foreground hover:text-destructive"
          title={connection.connected ? 'Desconectar integração' : 'Remover conexão'}
          onClick={() => onDisconnect(worker, connection)}
        >
          <Trash2 className="h-4 w-4 mr-1" />
          {connection.connected ? 'Desconectar' : 'Remover'}
        </Button>
      </div>
    </div>
  )
}

export default function ComposioWorkersTable({ workers, appSlug, canManage, connectable }: ComposioWorkersTableProps) {
  const [refreshingId, setRefreshingId] = useState<string | null>(null)
  const [connectingKey, setConnectingKey] = useState<string | null>(null)
  const [disconnectTarget, setDisconnectTarget] = useState<{
    worker: WorkerConnectionStatus
    connection: ComposioConnectionStatus
  } | null>(null)
  const didAutoRefresh = useRef(false)

  const autoRefreshConnection = useMemo(() => {
    return workers
      .flatMap((worker) => worker.connections)
      .find((connection) => shouldAutoRefresh(connection))
  }, [workers])

  useEffect(() => {
    const clearPendingConnect = () => setConnectingKey(null)
    window.addEventListener('focus', clearPendingConnect)
    return () => window.removeEventListener('focus', clearPendingConnect)
  }, [])

  useEffect(() => {
    if (!canManage || !autoRefreshConnection || didAutoRefresh.current) return

    didAutoRefresh.current = true
    handleRefresh(autoRefreshConnection, { automatic: true })
  }, [autoRefreshConnection, canManage])

  // Abre o diálogo de confirmação em vez de window.confirm: a ação é destrutiva
  // (revoga o acesso no Composio e apaga o registro local), então mostramos qual
  // conta e qual worker serão afetados antes de confirmar.
  const handleDisconnect = (worker: WorkerConnectionStatus, connection: ComposioConnectionStatus) => {
    setDisconnectTarget({ worker, connection })
  }

  const confirmDisconnect = () => {
    if (!disconnectTarget) return
    router.delete('/dashboard/composio/disconnect', {
      data: { connected_account_id: disconnectTarget.connection.connected_account_id },
      preserveScroll: true,
    })
    setDisconnectTarget(null)
  }

  const handleRefresh = (connection: ComposioConnectionStatus, options: { automatic?: boolean } = {}) => {
    setRefreshingId(connection.connected_account_id)
    submitComposioRefresh(connection.connected_account_id, {
      reloadProps: ['worker_statuses', 'any_connected'],
      onFinish: () => setRefreshingId(null),
      preserveState: true,
      silent: options.automatic,
    })
  }

  const handleConnect = (worker: WorkerConnectionStatus) => {
    const key = `connect:${worker.id}`
    setConnectingKey(key)
    submitComposioConnect(appSlug, worker.id)
  }

  const handleReconnect = (worker: WorkerConnectionStatus, connection: ComposioConnectionStatus) => {
    const key = `reconnect:${connection.connected_account_id}`
    setConnectingKey(key)
    submitComposioConnect(appSlug, worker.id, {
      accountLabel: connection.account_label,
      replacesConnectedAccountId: connection.connected_account_id,
    })
  }

  const disconnectVerb = disconnectTarget?.connection.connected ? 'Desconectar' : 'Remover'

  return (
    <>
      <div className="flex items-center gap-2 mb-4 text-sm font-medium text-foreground">
        <Users className="h-4 w-4" />
        Colaboradores digitais
      </div>

      {workers.length === 0 ? (
        <p className="text-sm text-muted-foreground py-4 text-center">
          Nenhum colaborador digital disponível.
        </p>
      ) : (
        <div className="space-y-3">
          {workers.map((worker) => (
            <section key={worker.id} className="rounded-lg border border-border bg-background p-4">
              <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                <Link
                  href={`/dashboard/digital_workers/${worker.id}`}
                  className="min-w-0 inline-flex items-center gap-3 rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
                >
                  <Avatar className="size-8 shrink-0 text-[12px]">
                    {worker.avatar_url ? <AvatarImage alt="" src={worker.avatar_url} /> : null}
                    <AvatarFallback className="bg-agent text-agent-foreground" style={agentHueStyle(worker.id)}>
                      {initialOf(worker.name)}
                    </AvatarFallback>
                  </Avatar>
                  <span className="min-w-0">
                    <span className="block truncate text-sm font-medium text-foreground">{worker.name}</span>
                    <span className="block truncate text-xs text-muted-foreground">{worker.job_title || '—'}</span>
                  </span>
                </Link>

                <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                  {statusBadge(worker.connection_status)}
                  {connectable && (
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!canManage || Boolean(connectingKey)}
                      onClick={() => handleConnect(worker)}
                    >
                      <Plug className="h-4 w-4 mr-1" />
                      {worker.connections.length > 0 ? 'Nova conexão' : 'Conectar'}
                    </Button>
                  )}
                </div>
              </div>

              <div className="mt-4 space-y-2">
                {worker.connections.length === 0 ? (
                  <p className="rounded-md border border-dashed border-border p-3 text-sm text-muted-foreground">
                    Nenhuma conexão ainda.
                  </p>
                ) : (
                  worker.connections.map((connection) => (
                    <ConnectionRow
                      key={connection.connected_account_id}
                      worker={worker}
                      connection={connection}
                      canManage={canManage}
                      refreshingId={refreshingId}
                      connectingKey={connectingKey}
                      onRefresh={handleRefresh}
                      onDisconnect={handleDisconnect}
                      onReconnect={handleReconnect}
                    />
                  ))
                )}
              </div>
            </section>
          ))}
        </div>
      )}

      <AlertDialog
        open={Boolean(disconnectTarget)}
        onOpenChange={(open) => {
          if (!open) setDisconnectTarget(null)
        }}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{disconnectVerb} conexão</AlertDialogTitle>
            <AlertDialogDescription>
              {disconnectTarget && (
                <>
                  Isso vai {disconnectVerb.toLowerCase()}{' '}
                  <span className="font-medium text-foreground">{disconnectTarget.connection.account_label}</span> de{' '}
                  <span className="font-medium text-foreground">{disconnectTarget.worker.name}</span>, revogar o acesso
                  no Composio e não pode ser desfeito.
                </>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction className={buttonVariants({ variant: 'destructive' })} onClick={confirmDisconnect}>
              {disconnectVerb}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
